/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import config from 'Cloud/config';
import { getViewerFor } from 'Cloud/ui/Viewer/viewerFactory';
import { path, pathEq } from 'ramda';
import { connect } from 'react-redux';
import { removeItems } from 'reactApp/appHelpers/appHelpers';
import { HIDE_ADS, IS_BLOCKED, IS_MOBILE_OR_TABLET_BROWSER, IS_ONPREMISE, IS_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { clearRenamedFileIdInsideViewer, getRenamedFileIdInsideViewer } from 'reactApp/appHelpers/updateRenamedFileInsideViewer';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { isDialogVisible } from 'reactApp/modules/dialog/dialog.selectors';
import { getEditorsFor } from 'reactApp/modules/editor/editor.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { selectFace } from 'reactApp/modules/faces/faces.module';
import { getFacesOnFile } from 'reactApp/modules/faces/faces.selectors';
import { IFaceWithCoordinates } from 'reactApp/modules/faces/faces.types';
import { FavoritesSelectors } from 'reactApp/modules/favorites/favorites.selectors';
import {
    getFeatureAttachesEnabled,
    getFeatureEditingNoneditableFormats,
    getFeatureMyOfficeViewerPromo,
    getFeatureNewAdFormat,
    getFeatureNormalizeConvertPdf,
    getFeaturePdfConverterStorage,
    getFeaturePdfEdit,
    getFeaturesAttachesTrialPromo,
    getFeatureShowEditBtnForSharedFolder,
    getFeatureShowEditButtonForAttach,
} from 'reactApp/modules/features/features.selectors';
import {
    getParent,
    getWeblinkFromPublicId,
    isDocument,
    isFileForPlayer,
    isImage,
    isPdfEditable,
    isPreviewable,
    isVideo,
} from 'reactApp/modules/file/utils';
import { isReadOnly, isThisOrParentMounted } from 'reactApp/modules/home/home.selectors';
import { HomeFile } from 'reactApp/modules/home/home.types';
import { addToFavorites, removeFromFavorites } from 'reactApp/modules/modifying/modifying.actions';
import { isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { requiredAuthorizationHelpers } from 'reactApp/modules/requiredAuthorization/helpers';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { newSendSearchRadarAnalytics } from 'reactApp/modules/search/search.analytics';
import { getSearchAnalyticsParams } from 'reactApp/modules/search/search.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getItemById, getStorageItemById, isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { markStoryAsViewed as markStoryAsViewedAction } from 'reactApp/modules/stories/stories.module';
import { getAdjacentStoryIds, getStoryForViewer } from 'reactApp/modules/stories/stories.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import {
    closeViewer,
    downloadArchiveItemRequest,
    openPdfEditor,
    publishItem,
    selectItemIndexInArray as selectItemIndexInArrayAction,
} from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { isVisiblePromo } from 'reactApp/ui/MyOfficePromo/ViewerSnackbar/ViewerSnackbarPromoContainer';
import { NUMBER_TO_PRELOAD } from 'reactApp/ui/ReactViewer/ReactViewer.constants';
import { getFitContentOption, getItemImageUrl, getPosterUrl, setFitContentOption } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { ReactViewerComponent } from 'reactApp/ui/ReactViewer/ReactViewerComponent';
import { TRIAL_ATTACHES_BANNER_ID } from 'reactApp/ui/TrialPromoBanner/TrialPromoBanner';
import { noopVoid } from 'reactApp/utils/helpers';
import { closeViewerUrl } from 'reactApp/utils/urlHelper';

/**
 * TypeGuard: проверка, что файл является приватным
 */
const isPrivateFile = (file: CloudItem): file is HomeFile => 'home' in file && file?.storage !== EStorageType.public;

// eslint-disable-next-line max-lines-per-function
const mapStateToProps = (state, props) => {
    const { isStock } = props;
    const promoTrialFeatures = getFeaturesAttachesTrialPromo(state);
    const file = ViewerSelectors.getViewerItem(state);
    const indexInArray = ViewerSelectors.getViewerItemIndexInArray(state);
    const itemStorage = ViewerSelectors.getViewerItemStorage(state) ?? file?.storage;
    const isActionOpen = ViewerSelectors.isViewerActionOpen(state);
    const ids = ViewerSelectors.getViewerItemIds(state);
    const itemCount = ViewerSelectors.getViewerItemCount(state);
    const hasMoreToLoad = ViewerSelectors.hasMoreToLoad(state);
    const gaSuffix = ViewerSelectors.getViewerGa(state);
    const activeArchiveItem = ViewerSelectors.getArchiveActiveItem(state);
    const isAuthorized = !UserSelectors.isAnonymous(state);
    const isAttachesEnabled = !!getFeatureAttachesEnabled(state);
    const preloadUrls: { url: string; id: string }[] = [];

    const editingNoneditableFormats =
        !!file && !!itemStorage && isPrivateFile(file)
            ? getFeatureEditingNoneditableFormats(state, { file, storage: itemStorage })
            : undefined;
    const isEditable = getEditorsFor(state, editingNoneditableFormats ? { ...file, ext: editingNoneditableFormats.ext } : file)?.length > 0;

    const email = UserSelectors.getEmail(state);
    const searchParams = getSearchAnalyticsParams(state);
    const isOwner = isOwnPublic(state);
    const isAttaches = itemStorage === EStorageType.attaches || itemStorage === EStorageType.viewerAttaches;
    const attachesSearchState = AttachesSelectors.getSearchState(state);
    const attachesQuery = attachesSearchState?.query;
    const attachesSearchedItem = file && !!attachesSearchState && AttachesSelectors.getAttachById(state, file.id);
    const attachesSearchedItemPos = attachesSearchedItem ? attachesSearchedItem?.pos : undefined;
    const isDialogShown = isDialogVisible(state);
    const isMountedFolder = isThisOrParentMounted(state, file?.parent);
    const hideAdsOnAttaches = UserSelectors.isHideAdInMail(state);
    const showBottomAds = itemStorage === EStorageType.public && !HIDE_ADS;
    const isNewAdFormat = getFeatureNewAdFormat(state);
    const pdfConverterStorage = getFeaturePdfConverterStorage(state, file);
    const pdfNormalizeConverterEnabled = getFeatureNormalizeConvertPdf(state, file);
    const disableClose = IS_BLOCKED;
    const isPublic = IS_PUBLIC;
    const isAttach = itemStorage === EStorageType.attaches || itemStorage === EStorageType.viewerAttaches;
    const isSearch = itemStorage === EStorageType.search;
    const attachType = file && 'type' in file && file.type;
    const isAttachesCloudStock = isAttach && attachType === EAttachTypes.cloudStock;
    const isAttachesCloud = isAttach && attachType === EAttachTypes.cloud;

    const ext = (file && 'ext' in file && file.ext) || '';
    const featureMyOfficePromoData = getFeatureMyOfficeViewerPromo(state);
    const viewerApi = getViewerFor(ext, isAttaches, itemStorage, file);
    const isMyOfficePromo = isVisiblePromo(viewerApi, featureMyOfficePromoData);

    const trialWasShown = UserStorageSelectors.get(state, TRIAL_ATTACHES_BANNER_ID);
    const showTrialPromo =
        !trialWasShown && Boolean(file) && !isMyOfficePromo && isAttaches && promoTrialFeatures && !isFileForPlayer(file);

    let faces: IFaceWithCoordinates[] = [];
    if (itemStorage === EStorageType.public) {
        faces = getFacesOnFile(state, file?.id?.replace(getWeblinkFromPublicId(file?.id) ?? '', ''));
    }

    let isPreviewableItem = isPreviewable(file, itemStorage);

    if (isAttaches && pathEq(['subKind'], 'archive', file) && !isAttachesCloudStock && !isAttachesCloud) {
        // В аттачах все просматриваем, идем в почтовое апи
        // На клауд стоки только зип, и  нужный isPreviewableItem уже выставлен.
        isPreviewableItem = attachType !== EAttachTypes.temporary;
    }

    if (isPreviewableItem && file?.kind === 'document' && isAttaches && !isAttachesCloudStock && !isAttachesCloud) {
        isPreviewableItem = config.get('DOCUMENTS_VIEW_O2_VIEWER')?.length > 0;
    }

    // Файл не надо увеличивать/уменьшать, если мы его не показываем
    const disableZoom = !isPreviewableItem || itemStorage === EStorageType.story;

    // Не показываем кнопку редактирования на аттачах типа cloud и cloud_stock
    const isAttachType = attachType === EAttachTypes.attach;
    const showEditButtonAttachCondition = isAttaches ? isAttachType && getFeatureShowEditButtonForAttach(state) : true;

    // Условие показа кнопки редактирования для файлов в шаренных папках
    const sharedFolderState = isMountedOrSharedFolder(state, file);
    const sharedFolderFile = sharedFolderState.isMounted;
    const sharedFolderFileBtnFeature = getFeatureShowEditBtnForSharedFolder(state);

    /**
     * Показ кнопки редактирования на mounted и (public не для владельцев) папках всегда регулируется фичей,
     * а для шареных папок показываем всегда (шареных значит для владельцев показываем всегда)
     */
    const showEditButtonForFileInSharedFolder = sharedFolderFile ? sharedFolderFileBtnFeature : true;

    // B2BCLOUD-416 скрываем кнопку для файлов из поиска, в случае, если они находятся в readonly папке
    const homeFileFolder = getParent(file?.home || '/');
    const isReadonlyHomeFolder = isReadOnly(state, homeFileFolder);
    const showEditButtonInRoFolderFromSearch = IS_ONPREMISE && isSearch ? !isReadonlyHomeFolder : true;
    // Выключаем кнопку на ONPREMISE, если папка readOnly
    const { folderId, isMounted } = isMountedOrSharedFolder(state, file);
    const folder = folderId && getItemById(state, folderId);
    const disableButtonInReadOnlyFolder = isMounted && folder && 'isReadOnly' in folder && folder.isReadOnly && IS_ONPREMISE;

    const allowEdit =
        !isPublic &&
        !isStock &&
        isPreviewableItem &&
        !path(['isReadOnly'], file) &&
        showEditButtonAttachCondition &&
        showEditButtonForFileInSharedFolder &&
        showEditButtonInRoFolderFromSearch &&
        !disableButtonInReadOnlyFolder;
    const showEditButton = (allowEdit || editingNoneditableFormats?.storage) && isEditable && isDocument(file);
    const showPdfEditButton =
        allowEdit && isPdfEditable(file) && getFeaturePdfEdit(state) && !(IS_MOBILE_OR_TABLET_BROWSER || EnvironmentSelectors.isIpadOs());

    // TODO: Move preloadUrls update to store or saga?
    // in viewer start and selectIndex?
    if (props.enablePreload && indexInArray !== null && itemStorage && ids) {
        for (let i = indexInArray; i < indexInArray + NUMBER_TO_PRELOAD && i < ids.length; i++) {
            const item = getStorageItemById(state, itemStorage, ids[i]);

            preloadUrls.push({
                url: isVideo(item) ? getPosterUrl(item) : isImage(item) && isPreviewable(item, itemStorage) ? getItemImageUrl(item) : '',
                id: item?.id || '',
            });
        }
    }

    let backItemIndexInArray: number | null = null;
    let forwardItemIndexInArray: number | null = null;
    if (indexInArray !== null && indexInArray !== undefined) {
        backItemIndexInArray = !!ids && indexInArray > 0 ? indexInArray - 1 : null;
        forwardItemIndexInArray = !!ids && indexInArray >= 0 && indexInArray + 1 < ids.length ? indexInArray + 1 : null;

        if (itemStorage === EStorageType.story) {
            const { prevStoryId, nextStoryId } = getAdjacentStoryIds(state);
            backItemIndexInArray = backItemIndexInArray ?? (prevStoryId ? -1 : null);
            forwardItemIndexInArray = forwardItemIndexInArray ?? (nextStoryId ? ids?.length ?? null : null);
        }
    }

    let storyType, bottomTitle, bottomSubTitle, topSubTitle, topTitle, storyId, isStoryViewed;
    if (itemStorage === EStorageType.story) {
        const data = getStoryForViewer(state, file?.id);
        storyType = data?.type;
        bottomTitle = data?.bottomTitle;
        bottomSubTitle = data?.bottomSubTitle;
        topTitle = data?.topTitle;
        topSubTitle = data?.topSubTitle;
        storyId = data?.id;
        isStoryViewed = data?.viewed;
    }

    const isSearchRadar = itemStorage === EStorageType.search || (!!attachesSearchedItem && isAttaches && !!attachesQuery);

    /* tempexp_15344-next-line */
    const isSuccessAuthWhenDownload = requiredAuthorizationHelpers.isSuccessAuthWhenDownload(state);

    return {
        file,
        activeArchiveItem,
        ids,
        indexInArray,
        itemStorage,
        backItemIndexInArray,
        forwardItemIndexInArray,
        preloadUrls,
        itemCount,
        hasMoreToLoad,
        gaSuffix: gaSuffix ? `_${gaSuffix}` : '',
        isActionOpen,
        isPreviewableItem,
        isAuthorized,
        isAttaches,
        getFitContentState: () => getFitContentOption(email),
        setFitContentState: (isFitContent) => setFitContentOption(email, isFitContent),
        sendSearchRadar: ({ dwhData, items }) => {
            if (isAttaches) {
                dwhData.attaches_query = attachesQuery;
            }

            newSendSearchRadarAnalytics({
                ...dwhData,
                searchParams,
                items,
            });
        },
        isAttachesEnabled,
        isDialogShown,
        isMountedFolder,
        hideAdsOnAttaches,
        showBottomAds,
        showEditButton,
        sharedFolderFile,
        sharedFolderFileBtnFeature,
        isEditable,
        pdfConverterStorage,
        editingNoneditableFormatsStorage: editingNoneditableFormats?.storage,
        pdfNormalizeConverterEnabled,

        storyId,
        storyType,
        bottomTitle,
        bottomSubTitle,
        topSubTitle,
        topTitle,
        disableZoom,
        isStoryViewed,
        faces,
        isNewAdFormat,
        isSearchRadar,
        showPdfEditButton,
        attachesSearchedItemPos,
        disableClose,
        isOwner,
        showTrialPromo,
        /* tempexp_15344-next-line */
        isSuccessAuthWhenDownload,
    };
};

const mapDispatchToProps = (dispatch, props) => ({
    showSnackbar: (data) => dispatch(showSnackbarAction(data)),
    publishItemAction: (data) => dispatch(publishItem(data)),
    markStoryAsViewed: ({ storyId, lastViewedId }) => dispatch(markStoryAsViewedAction({ storyId, lastViewedId })),
    selectItemIndexInArray: ({ itemIndex }) => dispatch(selectItemIndexInArrayAction({ itemIndex })),
    toggleFavorite: (item): void => {
        dispatch((_, getState) => {
            const state = getState();
            const storage = getCurrentStorage(state);

            const { isFavorites } = getStorage(storage);

            const favoritesStatistics = FavoritesSelectors.getFavoritesStatistics(state);

            /*
                Если в избранном всего один файл,
                то при удалении его из избранного через viewer нужно подчистить урл
            */
            if (isFavorites && item.isInFavorites && favoritesStatistics.all === 1) {
                const closeUrl = closeViewerUrl(location);
                // TODO: routing
                dispatch(historyPush({ id: closeUrl }));
            }

            if (item.isInFavorites) {
                dispatch(removeFromFavorites({ items: [item] }));
            } else {
                dispatch(
                    addToFavorites({
                        items: [item],
                        from: 'viewer',
                    })
                );
            }
        });
    },
    deleteFile: (item: CloudItem, itemStorage?: EStorageType, onSuccess = noopVoid) =>
        removeItems({
            items: [item],
            onSuccess,
        }),
    onClose: () => {
        if (typeof props.onClose === 'function') {
            props.onClose();
        }
        dispatch(closeViewer());
    },
    downloadArchiveItemAction: (data) => dispatch(downloadArchiveItemRequest(data)),
    selectFace: (id) => {
        dispatch(selectFace({ id }));
    },
    getRenamedFileIdInsideViewer: () => dispatch(getRenamedFileIdInsideViewer()),
    clearRenamedFileIdInsideViewer: () => dispatch(clearRenamedFileIdInsideViewer()),
    openPdfEditor: (file: any) => {
        if (!file?.home) {
            return;
        }
        dispatch(openPdfEditor({ id: file.home, storage: EStorageType.home }));
    },
});

export const ReactViewerConnected = connect(mapStateToProps, mapDispatchToProps)(ReactViewerComponent);
